import React from 'react';
import { useIntl } from 'ha/i18n';

import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { firstAvailableDay } from 'ha/utils/exclusionDate';
import { Grid } from '@ha/dsl';

import {
  isDayAfter,
  isDayBefore,
  isDayInMoveInWindow,
  RangeModifier,
} from 'ha/ui/next/DatePicker';

import {
  DATEPICKER_VARIANT,
  DateRangePickerProps,
  DateSelectionRules,
  FOCUSED_FIELD,
} from './types';
import calcFirstAvailableToDate from './helpers/calcFirstAvailableToDate';
import isFromDateDisabled from './helpers/isFromDateDisabled';
import isToDateDisabled from './helpers/isToDateDisabled';
import { OverlayWrapper } from './containers';
import DateRangePickerInputs from './components/DateRangePickerInputs';
import { DatePickerDialog, DatePickerPopover } from './Overlays';

const DateRangePicker: React.FC<DateRangePickerProps> = props => {
  const { T, formatDate, locale } = useIntl();

  const {
    AdditionalInfo = null,
    autoClose = true,
    noEndDate = false,
    noEndDateLabel = T('No end date'),
    CaptionSubtitle,
    DatePickerInputs = DateRangePickerInputs,
    value: { from, to },
    variant = DATEPICKER_VARIANT.INLINE,
    clearDatesButton,
    fromInputLabel = T('Select'),
    toInputLabel = T('Select'),
    fromEmptyValueLabel = T('Move-in date'),
    toEmptyValueLabel = T('Move-out date'),
    exclusions = [],
    datePickerProps = {},
    initialValidation = false,
    fromInputProps = {},
    toInputProps = {},
    onDateChange,
    onBlur,
    minimumPeriod = 0,
    moveInWindows = [],
    moveInWindowsTooltip = T('Please select the date within move-in period'),
    overlayCloseButton = T('Done'),
    buttonProps = {},
    required = false,
    minDate = new Date(),
    maxDate = null,
    header = null,
  } = props;

  const { sm } = useMediaQuery();
  const [enteredTo, setEnteredTo] = React.useState<Date | undefined>(to);
  const [focused, setFocused] = React.useState<FOCUSED_FIELD | null>(null);

  const today = new Date();

  const dateSelectionRules: DateSelectionRules = {
    minDate,
    maxDate,
  };

  const minimumPeriodHint = React.useMemo(
    () =>
      minimumPeriod
        ? T(
            'Minimum rental period: %s %s',
            minimumPeriod,
            minimumPeriod === 1 ? T('month') : T('months'),
          )
        : '',
    [T, minimumPeriod],
  );
  const moveInWindowsHint = React.useMemo(
    () =>
      moveInWindows.length > 0
        ? T(
            'First move-in period: %s till %s',
            formatDate('DD MMM YYYY', moveInWindows[0].from),
            formatDate('DD MMM YYYY', moveInWindows[0].to),
          )
        : '',
    [T, formatDate, moveInWindows],
  );

  React.useEffect(() => {
    if (from && initialValidation) {
      let isFromDateInvalid = isFromDateDisabled(
        from,
        exclusions,
        minimumPeriod,
        dateSelectionRules,
      );

      if (!isFromDateInvalid && moveInWindows.length > 0) {
        isFromDateInvalid = !isDayInMoveInWindow(moveInWindows, from);
      }
      let isToDateInvalid = false;

      if (to) {
        isToDateInvalid = isToDateDisabled(to, from, exclusions, minimumPeriod);
      }

      if (isFromDateInvalid || isToDateInvalid) {
        onDateChange({
          from: undefined,
          to: undefined,
        });
      }
    }
  }, []);

  React.useEffect(() => {
    // reset selected range if to becomes undefined (initial validation)
    if (!to && enteredTo) {
      setEnteredTo(undefined);
    }
  }, [to]);

  // whether a "From" date is disabled
  const isFromDisabled = (day: Date) =>
    isFromDateDisabled(day, exclusions, minimumPeriod, dateSelectionRules);
  // whether a "To" date is disabled
  const isToDisabled = (day: Date) =>
    isToDateDisabled(day, from, exclusions, minimumPeriod, dateSelectionRules);

  // From/To change handlers
  const onFromDateChange = React.useCallback(
    (date: Date) => {
      setFocused(FOCUSED_FIELD.TO);

      if (to) {
        if (isDayAfter(date, to)) {
          onDateChange({
            from: date,
            to: undefined,
          });

          setEnteredTo(undefined);

          return;
        }

        if (minimumPeriod) {
          const isDateDisabled = isToDateDisabled(
            to,
            date,
            exclusions,
            minimumPeriod,
          );

          if (isDateDisabled) {
            onDateChange({
              from: date,
              to: undefined,
            });

            setEnteredTo(undefined);

            return;
          }
        }
      }

      onDateChange({
        from: date,
        to,
      });
    },
    [to],
  );
  const onToDateChange = React.useCallback(
    (date: Date) => {
      if (from && isDayBefore(date, from)) {
        onDateChange({
          from: date,
          to: undefined,
        });

        setEnteredTo(undefined);
        setFocused(FOCUSED_FIELD.TO);

        return;
      }

      onDateChange({
        from,
        to: date,
      });
      setEnteredTo(date);

      setFocused(null);
    },
    [from, onDateChange],
  );

  const onMouseEnter = React.useCallback(
    (day: Date) => {
      if (!from) {
        return;
      }
      const isRangeSelected = from && to;

      if (isRangeSelected) {
        return;
      }
      if (isDayBefore(day, from)) {
        setEnteredTo(undefined);

        return;
      }
      if (!to) {
        setEnteredTo(day);
      }
    },
    [from, to],
  );

  const onDatesClear = React.useCallback(() => {
    onDateChange({
      from: undefined,
      to: undefined,
    });

    setEnteredTo(undefined);
    setFocused(FOCUSED_FIELD.FROM);
  }, [onDateChange]);

  const onOverlayExit = React.useCallback(() => {
    setEnteredTo(to);
    setFocused(null);
    if (onBlur) {
      onBlur();
    }
  }, [to, onBlur]);
  const onOverlayOpen = React.useCallback((field: FOCUSED_FIELD) => {
    setFocused(field);
  }, []);
  const onNoEndDate = React.useCallback(() => {
    onDateChange({
      from,
      to: undefined,
    });
    setFocused(null);
    setEnteredTo(undefined);
  }, [from, onDateChange]);

  // DatePickers props
  const firstAvailableDate = React.useMemo(
    () => new Date(firstAvailableDay(exclusions, minimumPeriod) || today),
    [],
  );
  const firstAvailableToDate = () => {
    if (to) {
      return to;
    }

    if (from && minimumPeriod) {
      return calcFirstAvailableToDate(from, minimumPeriod);
    }

    return from;
  };

  const commonDatePickerProps = {
    numberOfMonths: sm ? 2 : 1,
    locale,
    ...datePickerProps,
    selectedDays: [from, { from, to: enteredTo }] as RangeModifier[],
  };
  const fromDatePickerProps = {
    ...commonDatePickerProps,
    onDayClick: onFromDateChange,
    month: from || firstAvailableDate,
    modifiers: {
      start: from,
      end: enteredTo,
      disabled: isFromDisabled,
    },
  };
  const toDatePickerProps = {
    ...commonDatePickerProps,
    onDayClick: onToDateChange,
    month: firstAvailableToDate(),
    modifiers: {
      start: from,
      end: enteredTo,
      disabled: isToDisabled,
    },
    onDayMouseEnter: onMouseEnter,
  };

  const finalDatePickerProps =
    focused === FOCUSED_FIELD.FROM ? fromDatePickerProps : toDatePickerProps;

  // DatePicker Inputs props
  const fromDateInputProps = {
    fullWidth: true,
    'data-test-locator': 'DateRangePicker / From',
    ...fromInputProps,
    label: fromInputLabel,
    required,
  };
  const toDateInputProps = {
    fullWidth: true,
    'data-test-locator': 'DateRangePicker / To',
    ...toInputProps,
    label: toInputLabel,
    required,
  };

  const Wrappers = {
    [DATEPICKER_VARIANT.INLINE]: DatePickerPopover,
    [DATEPICKER_VARIANT.DIALOG]: DatePickerDialog,
    [DATEPICKER_VARIANT.RESPONSIVE]: sm ? DatePickerPopover : DatePickerDialog,
  };

  return (
    <Grid container justify="center">
      <OverlayWrapper
        AdditionalInfo={AdditionalInfo}
        autoClose={autoClose}
        buttonProps={buttonProps}
        noEndDate={noEndDate}
        noEndDateLabel={noEndDateLabel}
        CaptionSubtitle={CaptionSubtitle}
        DatePickerInputs={DatePickerInputs}
        focused={focused}
        fromInputProps={fromDateInputProps}
        toInputProps={toDateInputProps}
        fromEmptyValueLabel={fromEmptyValueLabel}
        toEmptyValueLabel={toEmptyValueLabel}
        datePickerProps={finalDatePickerProps}
        from={from}
        to={to}
        clearDatesButton={clearDatesButton}
        minimumPeriodHint={minimumPeriodHint}
        moveInWindows={moveInWindows}
        moveInWindowsTooltip={moveInWindowsTooltip}
        moveInWindowsHint={moveInWindowsHint}
        onDatesClear={onDatesClear}
        onNoEndDate={onNoEndDate}
        onOverlayExit={onOverlayExit}
        onOverlayOpen={onOverlayOpen}
        overlayCloseButton={overlayCloseButton}
        Overlay={Wrappers[variant]}
        header={header}
      />
    </Grid>
  );
};

export default DateRangePicker;
